/**
 * Which benefits the beta home may show, read from the rail's own rows.
 *
 * Both surfaces that render the page — the beta branch of `/dashboard` and the
 * full-page `/beta/home-preview` — build their flags here, from the same
 * `benefitRows(benefitFlags(...))` the side nav renders. The page used to read
 * partner entitlements directly, so a company that sold loan repayment put a
 * Loan Repayment card in front of an employee whose own plan never pays, and a
 * PSLF card in front of a TAS approver whose rail offered no benefits at all.
 *
 * Demo sessions flow through the same rows: `benefitFlags` takes the scenario,
 * so the rail and the page follow one selection.
 */
import type { BetaHomeFlags } from 'tio-employee/utils/beta/home-model';
import type FeaturesService from 'tio-common/services/features';
import type PartnerService from 'tio-employee/services/partner';
import type { BenefitRows } from 'tio-employee/utils/beta/benefit-rows';

export interface BetaHomeFlagSources {
  /** The rail's rows for this viewer, from `benefitRows(benefitFlags(...))`. */
  rows: BenefitRows;
  partner: PartnerService;
  features: FeaturesService;
}

/**
 * Maps the rail's rows onto the page's cards.
 *
 * @param sources the rows, the partner service and the features service
 * @returns one switch per card family the page can render
 */
export function betaHomeFlags({ rows, partner, features }: BetaHomeFlagSources): BetaHomeFlags {
  const settings = partner.companySettings;
  const isMember = rows.showMemberOnly || rows.isTaOnlyMember;

  return {
    showTuitionAssistance: rows.tuitionAssistance,
    showStudentLoanRepayment: rows.slr,
    showPslf: rows.pslf,
    showFinancialWellness: rows.wellness,
    showLpn: rows.learningPartners,

    /**
     * The one card without a rail row. The partner setting is the whole answer:
     * mentoring is bought separately from TA live, and the demo company settings
     * already derive it from the scenario (tuition assistance or career pathways).
     */
    showMentoring: isMember && (settings?.tuitionAssistance?.isTaMentoringEnabled ?? false),

    showSyf: rows.syf,
    showIdr: rows.idr,

    /**
     * Rides the overlay's surface rather than adding a sixth: the two coexist by
     * design and a company that wants one wants the other.
     */
    showPhoneCapture: features.can('phoneCapture.surfaces.postLoginOverlay'),
  };
}
